// "Pick the archive this pack serves" — the modal behind the Archive field on
// a route pack's edit screen.
//
// WHY THIS IS NOT ../file-selector, WHICH IS ALSO A FILE PICKER
//
// Because all three of that component's decisions are the wrong ones here, and
// each of them is load-bearing there rather than incidental:
//
//   1. `showableOf` gates on `isPreviewExt`, so it offers IMAGES and refuses
//      every document — a `.zip` renders as "Not an image a page can show".
//   2. It hands back the file's PUBLIC PATH, because what a page carries is a
//      URL. A pack carries a `fileId`: the bytes it serves are the EXTRACTED
//      tree, not the archive object, so a URL to the archive is not a thing
//      the mount could use.
//   3. It PUBLISHES a private file on select — correctly, because putting a
//      picture on a page is a person saying it is part of the website. That is
//      exactly wrong for an archive: mounting a pack publishes the extracted
//      bundle, and the .zip itself has no reason to become world-downloadable
//      as a side effect. The MOUNT is the publication decision here, and it is
//      the pack row that records it.
//
// So generalising that component would mean making its eligibility test, its
// return value and its button all injectable — which is most of it — for a
// second caller that shares only "a modal with a list of files in it". The
// archived project made exactly that trade with a Provider-fed generic picker
// and paid for it in indirection nothing used; ../file-selector's own header
// records that as the reason it stayed specific. This stays specific too.
import React, { useMemo, useRef } from "react";
import { Alert, Button } from "reactstrap";
import { Link } from "react-router-dom";
import * as ModalComp from "@repoflow/form-schema/modal";
import { List } from "@repoflow/components/list-cursor";
import { useSectionContext } from "@repoflow/components/section";
import * as SiteFileQL from "queries/site-file";
import SiteFileSchema from "@common/schema/site-file/public";
import { DOCUMENTS, KIND_CONFIG } from "@common/schema/site-file/kind";
import { isZipExt } from "@repoflow/common/file-utils";

// `status` earns its column here for a sharper reason than it does in the
// image picker. bucketws extracts an archive asynchronously after the upload,
// and pagews' copy of `status` and `size` is a SNAPSHOT taken when the row was
// last synced (docs/pagews-files.md §4) — so this column is the only thing on
// screen that says whether the tree behind the archive is ready yet.
const FIELDS = {
  "/Public/SiteFile": {
    fields: ["name", "ext", "size", "status"],
  },
};

// WHETHER A ROW COULD BACK A MOUNT AT ALL.
//
// Two facts about the file, neither of which a click can change.
//
// `isZipExt` because bucketws only extracts this one extension
// (projects/bucketws/api/handlers/upload-handler), so anything else has no
// tree behind it and never will — the model refuses one too, and says which
// of the two reasons it refused for.
//
// `size` and not `status`, the same gate ../file-selector uses and for the
// same reason it records: bucketws writes the object and the real size in one
// step and only then does the slow work, so a row can have perfectly good
// bytes while this service still has it at `processing`. Gating on the status
// snapshot would hide an archive that is in fact ready.
export const mountableOf = (file) => {
  const { fileId, ext, size } = file || {};

  return Boolean(fileId && ext && Number(size) > 0 && isZipExt(ext));
};

const Row = ({ item, onSelect, close }) => {
  if (!mountableOf(item)) {
    // Told which, rather than a disabled button with no explanation — the two
    // reasons resolve very differently. One is over in seconds; the other
    // never will be, because the file is not an archive.
    return (
      <span className="text-muted">
        {Number(item.size) > 0 ? "Not a .zip archive" : "Still uploading"}
      </span>
    );
  }

  return (
    <Button
      type="button"
      color="primary"
      onClick={() => {
        onSelect(item);
        close();
      }}
    >
      Select
    </Button>
  );
};

// Mounted only while the modal is open, so the listing query stays off every
// screen that merely has the picker on it.
const Body = ({ close, context: { onSelect, siteId } }) => {
  // Memoised on `siteId`, with both callbacks read through a ref — the same
  // shape and the same reason as ../file-selector's Body: `content` is
  // rendered by list-cursor as a component TYPE, so an inline arrow remounts
  // every row on each render of this body.
  const handlers = useRef({ onSelect, close });
  handlers.current = { onSelect, close };

  const Content = useMemo(
    () =>
      ({ item }) => (
        <Row
          item={item}
          onSelect={(...args) => handlers.current.onSelect(...args)}
          close={() => handlers.current.close()}
        />
      ),
    []
  );

  return (
    <List
      path={"viewer.account.sites.get.files"}
      query={SiteFileQL.list}
      variables={{ siteId, kind: DOCUMENTS }}
      schema={SiteFileSchema}
      target={{ $ref: "/Public/SiteFile" }}
      config={FIELDS}
      // An archive uploaded in another tab a moment ago should be here. "It is
      // not in the list" is indistinguishable from "the upload failed".
      fetchPolicy="cache-and-network"
      empty={
        <>
          No {KIND_CONFIG[DOCUMENTS].label.toLowerCase()} on this site yet.{" "}
          <Link to={`/site/${siteId}/file/${DOCUMENTS}`}>Upload one</Link>.
        </>
      }
      content={Content}
    />
  );
};

export const Component = ({ state, onSelect }) => {
  const context = useSectionContext();
  const siteId =
    context && context.attributes ? context.attributes.siteId : null;

  // The site route puts `siteId` in `attributes`, so a picker mounted outside
  // a site is a bug in whoever mounted it rather than a state to render
  // around. It says so instead of rendering nothing — a Select button that
  // opens an empty modal is the silently-absent Provider ../file-selector's
  // header describes gen-2 shipping.
  if (!siteId) {
    return (
      <ModalComp.Component state={state} title="Select an archive">
        {() => (
          <Alert color="danger">
            This picker is not inside a site, so it has nothing to list.
          </Alert>
        )}
      </ModalComp.Component>
    );
  }

  return (
    <ModalComp.Component
      state={state}
      title={"Select an archive"}
      context={{ onSelect, siteId }}
    >
      {Body}
    </ModalComp.Component>
  );
};

// `type="button"` because this sits inside a form, where the default is submit.
export const SelectButton = ({ onSelect, children = "Select", ...rest }) => {
  const state = ModalComp.useModalState(false);

  return (
    <>
      <Button
        type="button"
        color="primary"
        onClick={(e) => {
          e.preventDefault();
          state.open();
        }}
        {...rest}
      >
        {children}
      </Button>
      <Component state={state} onSelect={onSelect} />
    </>
  );
};
